import { HeroCardBody } from './HeroCardBody';
import { HeroCardCtas } from './HeroCardCtas';
import { HeroCardOnlineTab } from './HeroCardOnlineTab';
import { HeroCardPanel } from './HeroCardPanel';
import { HeroCardPostCta } from './HeroCardPostCta';
import { HeroCardSearch } from './HeroCardSearch';
import { HeroCardTabs } from './HeroCardTabs';
import { resolveHeroCardTabbedHome } from './resolveHeroCard';
import type { HeroCardTabbedHomeConfig, HeroCardRootProps } from './types';
import { Text } from '@/lib/render/Text';

export interface HeroCardTabbedHomeProps extends HeroCardRootProps {
  config: HeroCardTabbedHomeConfig;
}

/**
 * The homepage's tabbed card. Tab 1 ("Nachhilfe vor Ort") holds a title, the
 * PLZ finder, a rich-text body and two CTAs rendered inline; tab 2 is the
 * shared "Nachhilfe Online" body.
 *
 * Separate from the City card because the two tab-1 bodies differ structurally
 * — only this one carries a location search and its own submit tracking, and a
 * city hero with a finder inside tab 1 is a combination no design validates.
 */
export function HeroCardTabbedHome({
  config,
  ...rest
}: HeroCardTabbedHomeProps) {
  const resolved = resolveHeroCardTabbedHome(config);

  return (
    <HeroCardTabs
      tabOneLabel={resolved.tabOneLabel}
      tabOneAnchor={resolved.tabOneAnchor}
      tabTwoLabel={resolved.tabTwo.label}
      tabTwoAnchor={resolved.tabTwo.anchor}
      preselectedTab={resolved.preselectedTab}
      {...rest}
      renderTabOne={() => (
        <HeroCardPanel roundedTop={false}>
          {resolved.tabOneTitle && (
            <Text
              as="h2"
              field="tabOneTitle"
              className="text-secondary text-sm leading-snug font-medium md:text-lg md:leading-[1.1667]"
            >
              {resolved.tabOneTitle}
            </Text>
          )}
          <HeroCardSearch search={resolved.tabOneSearch} />
          <HeroCardBody nodes={resolved.tabOneBody} />
          <HeroCardCtas actions={resolved.tabOneCtas} layout="inline" />
          <HeroCardPostCta postCta={resolved.tabOnePostCta} />
        </HeroCardPanel>
      )}
      renderTabTwo={() => (
        <HeroCardPanel roundedTop={false}>
          <HeroCardOnlineTab tab={resolved.tabTwo} />
        </HeroCardPanel>
      )}
    />
  );
}
